import React, { useState, useEffect, useMemo } from "react";
import { useAuth, API } from "@/App";
import { useTranslation } from "react-i18next";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { 
  Plus, 
  Search, 
  Edit, 
  Trash2, 
  X, 
  RefreshCw, 
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Filter,
  Eye,
  FileText,
  Calendar as CalendarIcon,
  Receipt,
  CreditCard,
  AlertTriangle,
  Check,
  BookOpen,
  DollarSign,
  Clock,
  TrendingUp
} from "lucide-react";
import axios from "axios";
import { format, addDays } from "date-fns";
import { sv } from "date-fns/locale";

const SupplierInvoices = () => {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [invoices, setInvoices] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [payDialogOpen, setPayDialogOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [detailInvoice, setDetailInvoice] = useState(null);
  
  // Multi-select
  const [selectedInvoices, setSelectedInvoices] = useState(new Set());
  
  // Sorting
  const [sortColumn, setSortColumn] = useState("invoice_date");
  const [sortDirection, setSortDirection] = useState("desc");
  
  // Filters
  const [filterStatus, setFilterStatus] = useState("all");
  
  const [formData, setFormData] = useState({
    supplier_id: "",
    invoice_number: "",
    invoice_date: format(new Date(), "yyyy-MM-dd"),
    due_date: format(addDays(new Date(), 30), "yyyy-MM-dd"),
    ocr_number: "",
    lines: [{ description: "", quantity: 1, unit: "st", unit_price: 0, vat_rate: 25, account_number: "4000" }],
    notes: "",
    currency: "SEK"
  });
  
  const [paymentData, setPaymentData] = useState({
    amount: 0,
    date: format(new Date(), "yyyy-MM-dd")
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [invoicesRes, suppliersRes, statsRes] = await Promise.all([
        axios.get(`${API}/supplier-invoices`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/suppliers`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/supplier-invoices/stats`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setInvoices(invoicesRes.data);
      setSuppliers(suppliersRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error(t('supplierInvoices.fetchError'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.supplier_id) {
      toast.error(t('supplierInvoices.selectSupplier'));
      return;
    }
    
    try {
      if (editingInvoice) {
        await axios.put(`${API}/supplier-invoices/${editingInvoice.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(t('supplierInvoices.updated'));
      } else {
        await axios.post(`${API}/supplier-invoices`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(t('supplierInvoices.created'));
      }
      fetchData();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('supplierInvoices.saveFailed'));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('supplierInvoices.confirmDelete'))) return;
    try {
      await axios.delete(`${API}/supplier-invoices/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('supplierInvoices.deleted'));
      fetchData();
    } catch (error) {
      toast.error(t('supplierInvoices.deleteFailed'));
    }
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(t('supplierInvoices.confirmBulkDelete', { count: selectedInvoices.size }))) return;
    
    try {
      await Promise.all(
        Array.from(selectedInvoices).map(id =>
          axios.delete(`${API}/supplier-invoices/${id}`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        )
      );
      toast.success(t('supplierInvoices.bulkDeleted', { count: selectedInvoices.size }));
      setSelectedInvoices(new Set());
      fetchData();
    } catch (error) {
      toast.error(t('supplierInvoices.bulkDeleteFailed'));
    }
  };

  const handlePayment = async () => {
    if (!selectedInvoice) return;
    
    try {
      await axios.post(`${API}/supplier-invoices/${selectedInvoice.id}/pay`, paymentData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('supplierInvoices.paymentRegistered'));
      setPayDialogOpen(false);
      setSelectedInvoice(null);
      fetchData();
    } catch (error) {
      toast.error(t('supplierInvoices.paymentFailed'));
    }
  };

  const handleBook = async (invoice) => {
    if (invoice.booked) {
      toast.info(t('supplierInvoices.alreadyBooked'));
      return;
    }
    
    try {
      const res = await axios.post(`${API}/supplier-invoices/${invoice.id}/book`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('supplierInvoices.booked', { number: res.data.entry_number }));
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('supplierInvoices.bookFailed'));
    }
  };

  const resetForm = () => {
    setFormData({
      supplier_id: "",
      invoice_number: "",
      invoice_date: format(new Date(), "yyyy-MM-dd"),
      due_date: format(addDays(new Date(), 30), "yyyy-MM-dd"),
      ocr_number: "",
      lines: [{ description: "", quantity: 1, unit: "st", unit_price: 0, vat_rate: 25, account_number: "4000" }],
      notes: "",
      currency: "SEK"
    });
    setEditingInvoice(null);
  };

  const handleEdit = (invoice) => {
    setEditingInvoice(invoice);
    setFormData({
      supplier_id: invoice.supplier_id,
      invoice_number: invoice.invoice_number,
      invoice_date: invoice.invoice_date,
      due_date: invoice.due_date,
      ocr_number: invoice.ocr_number || "",
      lines: invoice.lines?.length > 0 ? invoice.lines : [{ description: "", quantity: 1, unit: "st", unit_price: 0, vat_rate: 25, account_number: "4000" }],
      notes: invoice.notes || "",
      currency: invoice.currency || "SEK"
    });
    setDialogOpen(true);
  };

  const openPayDialog = (invoice) => {
    setSelectedInvoice(invoice);
    setPaymentData({
      amount: invoice.total - (invoice.paid_amount || 0),
      date: format(new Date(), "yyyy-MM-dd")
    });
    setPayDialogOpen(true);
  };

  const addLine = () => {
    setFormData({
      ...formData,
      lines: [...formData.lines, { description: "", quantity: 1, unit: "st", unit_price: 0, vat_rate: 25, account_number: "4000" }]
    });
  };

  const removeLine = (index) => {
    if (formData.lines.length <= 1) return;
    setFormData({
      ...formData,
      lines: formData.lines.filter((_, i) => i !== index)
    });
  };

  const updateLine = (index, field, value) => {
    const newLines = [...formData.lines];
    newLines[index][field] = value;
    setFormData({ ...formData, lines: newLines });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("sv-SE", {
      style: "currency",
      currency: "SEK",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  // Sorting
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("desc");
    }
  };

  const SortIcon = ({ column }) => {
    if (sortColumn !== column) return <ArrowUpDown size={14} className="ml-1 opacity-50" />;
    return sortDirection === "asc" 
      ? <ArrowUp size={14} className="ml-1" /> 
      : <ArrowDown size={14} className="ml-1" />;
  };

  // Selection
  const toggleSelectAll = () => {
    if (selectedInvoices.size === filteredAndSortedInvoices.length) {
      setSelectedInvoices(new Set());
    } else {
      setSelectedInvoices(new Set(filteredAndSortedInvoices.map(i => i.id)));
    }
  };

  const toggleSelect = (id) => {
    const newSelected = new Set(selectedInvoices);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedInvoices(newSelected);
  };

  // Filter and sort
  const filteredAndSortedInvoices = useMemo(() => {
    let result = [...invoices];
    
    // Global search
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(inv => 
        inv.supplier_name?.toLowerCase().includes(term) ||
        inv.invoice_number?.toLowerCase().includes(term) ||
        inv.ocr_number?.toLowerCase().includes(term)
      );
    }
    
    // Status filter
    if (filterStatus !== "all") {
      result = result.filter(inv => inv.status === filterStatus);
    }
    
    // Sort
    result.sort((a, b) => {
      let aVal = a[sortColumn];
      let bVal = b[sortColumn];
      
      if (typeof aVal === "string") aVal = aVal?.toLowerCase() || "";
      if (typeof bVal === "string") bVal = bVal?.toLowerCase() || "";
      
      if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
    
    return result;
  }, [invoices, searchTerm, filterStatus, sortColumn, sortDirection]);

  // Calculate form totals
  const formTotals = useMemo(() => {
    const subtotal = formData.lines.reduce((sum, line) => sum + (line.quantity * line.unit_price), 0);
    const vat = formData.lines.reduce((sum, line) => sum + (line.quantity * line.unit_price * (line.vat_rate / 100)), 0);
    return { subtotal, vat, total: subtotal + vat };
  }, [formData.lines]);

  const getStatusBadge = (status) => {
    const badges = {
      unpaid: <Badge className="bg-amber-100 text-amber-700">{t('supplierInvoices.status.unpaid')}</Badge>,
      partially_paid: <Badge className="bg-blue-100 text-blue-700">{t('supplierInvoices.status.partiallyPaid')}</Badge>,
      paid: <Badge className="bg-green-100 text-green-700">{t('supplierInvoices.status.paid')}</Badge>,
      overdue: <Badge className="bg-red-100 text-red-700">{t('supplierInvoices.status.overdue')}</Badge>
    };
    return badges[status] || <Badge variant="outline">{status}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="supplier-invoices">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">{t('supplierInvoices.title')}</h1>
          <p className="text-slate-500 mt-1">{invoices.length} {t('supplierInvoices.registered')}</p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={fetchData} data-testid="refresh-btn">
            <RefreshCw size={16} className="mr-2" />
            {t('common.refresh')}
          </Button>
          <Button 
            className="bg-indigo-600 hover:bg-indigo-700" 
            onClick={() => { resetForm(); setDialogOpen(true); }}
            data-testid="new-invoice-btn"
          >
            <Plus size={16} className="mr-2" />
            {t('supplierInvoices.newInvoice')}
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="card-base">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">{t('supplierInvoices.stats.total')}</p>
                <p className="text-2xl font-bold">{formatCurrency(stats.total_amount)}</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                <Receipt className="text-indigo-600" size={20} />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="card-base">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">{t('supplierInvoices.stats.unpaid')}</p>
                <p className="text-2xl font-bold text-amber-600">{formatCurrency(stats.unpaid_amount)}</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <Clock className="text-amber-600" size={20} />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="card-base">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">{t('supplierInvoices.stats.overdue')}</p>
                <p className="text-2xl font-bold text-red-600">{formatCurrency(stats.overdue_amount)}</p>
                <p className="text-xs text-slate-400">{stats.overdue_count} {t('supplierInvoices.invoices')}</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                <AlertTriangle className="text-red-600" size={20} />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="card-base">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">{t('supplierInvoices.stats.thisMonth')}</p>
                <p className="text-2xl font-bold">{formatCurrency(stats.this_month_amount)}</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <TrendingUp className="text-green-600" size={20} />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Bulk Actions */}
      {selectedInvoices.size > 0 && (
        <Card className="card-base border-indigo-200 bg-indigo-50">
          <CardContent className="p-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-indigo-700">
                {selectedInvoices.size} {t('common.selected')}
              </span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setSelectedInvoices(new Set())}>
                  <X size={14} className="mr-1" />
                  {t('common.clearSelection')}
                </Button>
                <Button variant="destructive" size="sm" onClick={handleBulkDelete}>
                  <Trash2 size={14} className="mr-1" />
                  {t('common.delete')}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters & Search */}
      <Card className="card-base">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <Input
                placeholder={t('supplierInvoices.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-invoices"
              />
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[180px]">
                <Filter size={14} className="mr-2" />
                <SelectValue placeholder={t('supplierInvoices.filterByStatus')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('common.all')}</SelectItem>
                <SelectItem value="unpaid">{t('supplierInvoices.status.unpaid')}</SelectItem>
                <SelectItem value="partially_paid">{t('supplierInvoices.status.partiallyPaid')}</SelectItem>
                <SelectItem value="paid">{t('supplierInvoices.status.paid')}</SelectItem>
                <SelectItem value="overdue">{t('supplierInvoices.status.overdue')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Invoices Table */}
      <Card className="card-base overflow-hidden" data-testid="invoices-table">
        <CardContent className="p-0">
          {filteredAndSortedInvoices.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="p-3 w-10">
                      <Checkbox 
                        checked={selectedInvoices.size === filteredAndSortedInvoices.length && filteredAndSortedInvoices.length > 0}
                        onCheckedChange={toggleSelectAll}
                      />
                    </th>
                    <th className="text-left text-xs font-medium text-slate-500 uppercase p-3">
                      <button onClick={() => handleSort("invoice_number")} className="flex items-center hover:text-slate-700">
                        {t('supplierInvoices.invoiceNumber')}
                        <SortIcon column="invoice_number" />
                      </button>
                    </th>
                    <th className="text-left text-xs font-medium text-slate-500 uppercase p-3">
                      <button onClick={() => handleSort("supplier_name")} className="flex items-center hover:text-slate-700">
                        {t('supplierInvoices.supplier')}
                        <SortIcon column="supplier_name" />
                      </button>
                    </th>
                    <th className="text-left text-xs font-medium text-slate-500 uppercase p-3">
                      <button onClick={() => handleSort("invoice_date")} className="flex items-center hover:text-slate-700">
                        {t('supplierInvoices.invoiceDate')}
                        <SortIcon column="invoice_date" />
                      </button>
                    </th>
                    <th className="text-left text-xs font-medium text-slate-500 uppercase p-3">
                      <button onClick={() => handleSort("due_date")} className="flex items-center hover:text-slate-700">
                        {t('supplierInvoices.dueDate')}
                        <SortIcon column="due_date" />
                      </button>
                    </th>
                    <th className="text-right text-xs font-medium text-slate-500 uppercase p-3">
                      <button onClick={() => handleSort("total")} className="flex items-center justify-end hover:text-slate-700 ml-auto">
                        {t('common.total')}
                        <SortIcon column="total" />
                      </button>
                    </th>
                    <th className="text-center text-xs font-medium text-slate-500 uppercase p-3">
                      {t('common.status')}
                    </th>
                    <th className="text-center text-xs font-medium text-slate-500 uppercase p-3">
                      {t('supplierInvoices.booked')}
                    </th>
                    <th className="w-32"></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAndSortedInvoices.map((invoice) => (
                    <tr 
                      key={invoice.id} 
                      className="border-b hover:bg-slate-50 transition-colors"
                      data-testid={`invoice-row-${invoice.id}`}
                    >
                      <td className="p-3">
                        <Checkbox 
                          checked={selectedInvoices.has(invoice.id)}
                          onCheckedChange={() => toggleSelect(invoice.id)}
                        />
                      </td>
                      <td className="p-3 font-mono text-sm font-medium text-indigo-600">
                        {invoice.invoice_number}
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center text-white font-medium text-sm">
                            {invoice.supplier_name?.charAt(0)?.toUpperCase() || "?"}
                          </div>
                          <span className="font-medium">{invoice.supplier_name}</span>
                        </div>
                      </td>
                      <td className="p-3 text-slate-600">{invoice.invoice_date}</td>
                      <td className="p-3 text-slate-600">{invoice.due_date}</td>
                      <td className="p-3 text-right font-mono font-medium">
                        {formatCurrency(invoice.total)}
                      </td>
                      <td className="p-3 text-center">
                        {getStatusBadge(invoice.status)}
                      </td>
                      <td className="p-3 text-center">
                        {invoice.booked ? (
                          <Check size={16} className="mx-auto text-green-600" />
                        ) : (
                          <span className="text-slate-300">—</span>
                        )}
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-1 justify-end">
                          <Button variant="ghost" size="sm" onClick={() => setDetailInvoice(invoice)} title={t('common.view')}>
                            <Eye size={14} />
                          </Button>
                          {!invoice.booked && (
                            <Button variant="ghost" size="sm" onClick={() => handleBook(invoice)} title={t('supplierInvoices.bookInvoice')} className="text-blue-600">
                              <BookOpen size={14} />
                            </Button>
                          )}
                          {invoice.status !== "paid" && (
                            <Button variant="ghost" size="sm" onClick={() => openPayDialog(invoice)} title={t('supplierInvoices.registerPayment')} className="text-green-600">
                              <CreditCard size={14} />
                            </Button>
                          )}
                          <Button variant="ghost" size="sm" onClick={() => handleEdit(invoice)} title={t('common.edit')}>
                            <Edit size={14} />
                          </Button>
                          <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDelete(invoice.id)} title={t('common.delete')}>
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-16">
              <Receipt className="mx-auto text-slate-300 mb-4" size={48} />
              <p className="text-slate-500">{t('supplierInvoices.noInvoices')}</p>
              <Button 
                className="mt-4 bg-indigo-600 hover:bg-indigo-700" 
                onClick={() => { resetForm(); setDialogOpen(true); }}
              >
                <Plus size={16} className="mr-2" />
                {t('supplierInvoices.addFirst')}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText size={20} />
              {editingInvoice ? t('supplierInvoices.editInvoice') : t('supplierInvoices.newInvoice')}
            </DialogTitle>
            <DialogDescription>
              {editingInvoice ? t('supplierInvoices.editDescription') : t('supplierInvoices.createDescription')}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('supplierInvoices.supplier')} *</Label>
                <Select value={formData.supplier_id} onValueChange={(v) => setFormData({ ...formData, supplier_id: v })}>
                  <SelectTrigger data-testid="supplier-select">
                    <SelectValue placeholder={t('supplierInvoices.selectSupplier')} />
                  </SelectTrigger>
                  <SelectContent>
                    {suppliers.map(s => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t('supplierInvoices.invoiceNumber')} *</Label>
                <Input
                  value={formData.invoice_number}
                  onChange={(e) => setFormData({ ...formData, invoice_number: e.target.value })}
                  placeholder="F12345"
                  required
                  data-testid="invoice-number-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>{t('supplierInvoices.invoiceDate')}</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left font-normal">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {formData.invoice_date}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={new Date(formData.invoice_date)}
                      onSelect={(date) => date && setFormData({ ...formData, invoice_date: format(date, "yyyy-MM-dd") })}
                      locale={sv}
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-2">
                <Label>{t('supplierInvoices.dueDate')}</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left font-normal">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {formData.due_date}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={new Date(formData.due_date)}
                      onSelect={(date) => date && setFormData({ ...formData, due_date: format(date, "yyyy-MM-dd") })}
                      locale={sv}
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-2">
                <Label>OCR / {t('supplierInvoices.reference')}</Label>
                <Input
                  value={formData.ocr_number}
                  onChange={(e) => setFormData({ ...formData, ocr_number: e.target.value })}
                  placeholder="1234567890"
                  data-testid="ocr-input"
                />
              </div>
            </div>

            {/* Invoice Lines */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>{t('supplierInvoices.lines')}</Label>
                <Button type="button" variant="outline" size="sm" onClick={addLine}>
                  <Plus size={14} className="mr-1" />
                  {t('supplierInvoices.addLine')}
                </Button>
              </div>
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="text-left text-xs font-medium text-slate-500 uppercase p-2">{t('common.description')}</th>
                      <th className="text-right text-xs font-medium text-slate-500 uppercase p-2 w-20">{t('supplierInvoices.qty')}</th>
                      <th className="text-right text-xs font-medium text-slate-500 uppercase p-2 w-24">{t('supplierInvoices.price')}</th>
                      <th className="text-right text-xs font-medium text-slate-500 uppercase p-2 w-20">{t('supplierInvoices.vat')}</th>
                      <th className="text-center text-xs font-medium text-slate-500 uppercase p-2 w-24">{t('supplierInvoices.account')}</th>
                      <th className="w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {formData.lines.map((line, index) => (
                      <tr key={index} className="border-t">
                        <td className="p-2">
                          <Input
                            value={line.description}
                            onChange={(e) => updateLine(index, "description", e.target.value)}
                            placeholder={t('supplierInvoices.descriptionPlaceholder')}
                            className="h-8"
                          />
                        </td>
                        <td className="p-2">
                          <Input
                            type="number"
                            value={line.quantity}
                            onChange={(e) => updateLine(index, "quantity", parseFloat(e.target.value) || 0)}
                            className="h-8 text-right"
                            step="0.01"
                          />
                        </td>
                        <td className="p-2">
                          <Input
                            type="number"
                            value={line.unit_price}
                            onChange={(e) => updateLine(index, "unit_price", parseFloat(e.target.value) || 0)}
                            className="h-8 text-right"
                            step="0.01"
                          />
                        </td>
                        <td className="p-2">
                          <Select 
                            value={String(line.vat_rate)} 
                            onValueChange={(v) => updateLine(index, "vat_rate", parseFloat(v))}
                          >
                            <SelectTrigger className="h-8">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="25">25%</SelectItem>
                              <SelectItem value="12">12%</SelectItem>
                              <SelectItem value="6">6%</SelectItem>
                              <SelectItem value="0">0%</SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="p-2">
                          <Input
                            value={line.account_number}
                            onChange={(e) => updateLine(index, "account_number", e.target.value)}
                            className="h-8 text-center font-mono"
                            placeholder="4000"
                          />
                        </td>
                        <td className="p-2">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeLine(index)}
                            className="text-red-500 h-8 w-8 p-0"
                            disabled={formData.lines.length <= 1}
                          >
                            <Trash2 size={14} />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {/* Totals */}
              <div className="flex justify-end">
                <div className="w-64 space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-500">{t('supplierInvoices.subtotal')}:</span>
                    <span className="font-mono">{formatCurrency(formTotals.subtotal)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">{t('supplierInvoices.vatAmount')}:</span>
                    <span className="font-mono">{formatCurrency(formTotals.vat)}</span>
                  </div>
                  <div className="flex justify-between font-bold text-base border-t pt-1">
                    <span>{t('common.total')}:</span>
                    <span className="font-mono">{formatCurrency(formTotals.total)}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label>{t('supplierInvoices.notes')}</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder={t('supplierInvoices.notesPlaceholder')}
                rows={2}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-invoice-btn">
                {editingInvoice ? t('common.save') : t('supplierInvoices.createInvoice')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog open={payDialogOpen} onOpenChange={setPayDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard size={20} />
              {t('supplierInvoices.registerPayment')}
            </DialogTitle>
            <DialogDescription>
              {t('supplierInvoices.paymentFor')} {selectedInvoice?.invoice_number}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="p-4 bg-slate-50 rounded-lg space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">{t('supplierInvoices.invoiceTotal')}:</span>
                <span className="font-mono">{formatCurrency(selectedInvoice?.total)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">{t('supplierInvoices.alreadyPaid')}:</span>
                <span className="font-mono">{formatCurrency(selectedInvoice?.paid_amount)}</span>
              </div>
              <div className="flex justify-between font-medium border-t pt-2">
                <span>{t('supplierInvoices.remaining')}:</span>
                <span className="font-mono">{formatCurrency((selectedInvoice?.total || 0) - (selectedInvoice?.paid_amount || 0))}</span>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>{t('supplierInvoices.paymentAmount')}</Label>
              <Input
                type="number"
                value={paymentData.amount}
                onChange={(e) => setPaymentData({ ...paymentData, amount: parseFloat(e.target.value) || 0 })}
                step="0.01"
              />
            </div>
            
            <div className="space-y-2">
              <Label>{t('supplierInvoices.paymentDate')}</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start text-left font-normal">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {paymentData.date}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={new Date(paymentData.date)}
                    onSelect={(date) => date && setPaymentData({ ...paymentData, date: format(date, "yyyy-MM-dd") })}
                    locale={sv}
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setPayDialogOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handlePayment} className="bg-green-600 hover:bg-green-700">
              <Check size={14} className="mr-2" />
              {t('supplierInvoices.confirmPayment')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={!!detailInvoice} onOpenChange={(open) => !open && setDetailInvoice(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt size={20} />
              {t('supplierInvoices.invoiceDetails')} - {detailInvoice?.invoice_number}
            </DialogTitle>
          </DialogHeader>
          
          {detailInvoice && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-slate-500">{t('supplierInvoices.supplier')}</p>
                  <p className="font-medium">{detailInvoice.supplier_name}</p>
                </div>
                <div>
                  <p className="text-slate-500">{t('common.status')}</p>
                  <div className="mt-1">{getStatusBadge(detailInvoice.status)}</div>
                </div>
                <div>
                  <p className="text-slate-500">{t('supplierInvoices.invoiceDate')}</p>
                  <p className="font-medium">{detailInvoice.invoice_date}</p>
                </div>
                <div>
                  <p className="text-slate-500">{t('supplierInvoices.dueDate')}</p>
                  <p className="font-medium">{detailInvoice.due_date}</p>
                </div>
                {detailInvoice.ocr_number && (
                  <div>
                    <p className="text-slate-500">OCR</p>
                    <p className="font-mono">{detailInvoice.ocr_number}</p>
                  </div>
                )}
              </div>

              {/* Lines */}
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="text-left p-2">{t('common.description')}</th>
                      <th className="text-right p-2">{t('supplierInvoices.qty')}</th>
                      <th className="text-right p-2">{t('supplierInvoices.price')}</th>
                      <th className="text-right p-2">{t('common.total')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detailInvoice.lines?.map((line, idx) => (
                      <tr key={idx} className="border-t">
                        <td className="p-2">{line.description}</td>
                        <td className="p-2 text-right">{line.quantity}</td>
                        <td className="p-2 text-right font-mono">{formatCurrency(line.unit_price)}</td>
                        <td className="p-2 text-right font-mono">{formatCurrency(line.total || line.quantity * line.unit_price)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-slate-50 font-medium">
                    <tr className="border-t">
                      <td colSpan="3" className="p-2 text-right">{t('supplierInvoices.subtotal')}:</td>
                      <td className="p-2 text-right font-mono">{formatCurrency(detailInvoice.subtotal)}</td>
                    </tr>
                    <tr>
                      <td colSpan="3" className="p-2 text-right">{t('supplierInvoices.vatAmount')}:</td>
                      <td className="p-2 text-right font-mono">{formatCurrency(detailInvoice.vat_amount)}</td>
                    </tr>
                    <tr className="text-base">
                      <td colSpan="3" className="p-2 text-right font-bold">{t('common.total')}:</td>
                      <td className="p-2 text-right font-mono font-bold">{formatCurrency(detailInvoice.total)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {detailInvoice.notes && (
                <div>
                  <p className="text-slate-500 text-sm">{t('supplierInvoices.notes')}</p>
                  <p className="text-sm mt-1">{detailInvoice.notes}</p>
                </div>
              )}

              <DialogFooter>
                <Button variant="outline" onClick={() => setDetailInvoice(null)}>
                  {t('common.close')}
                </Button>
                <Button onClick={() => { handleEdit(detailInvoice); setDetailInvoice(null); }}>
                  <Edit size={14} className="mr-2" />
                  {t('common.edit')}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SupplierInvoices;
